import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, CheckCircle2, AlertTriangle } from 'lucide-react';
interface TrendItem {
  type: string;
  count: number;
  change: number;
}
interface TrendSectionProps {
  improved: TrendItem[];
  stable: TrendItem[];
  worsened: TrendItem[];
}
export function TrendSection({
  improved,
  stable,
  worsened
}: TrendSectionProps) {
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const ColumnHeader = ({
    title,
    icon: Icon,
    colorClass,
    bgClass
  }: {
    title: string;
    icon: any;
    colorClass: string;
    bgClass: string;
  }) => <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/5" data-id="element-1215">
      <div className={`p-2.5 rounded-xl ${bgClass} ${colorClass}`} data-id="element-1216">
        <Icon size={20} strokeWidth={2.5} data-id="element-1217" />
      </div>
      <h3 className={`text-lg font-bold ${colorClass}`} data-id="element-1218">{title}</h3>
    </div>;
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-7xl mx-auto px-4" data-id="element-1219">
      {/* Improved Column */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-50px'
    }} className="bg-gray-900/40 backdrop-blur-sm rounded-3xl p-6 border border-white/5" data-id="element-1220">
        <ColumnHeader title="Improved" icon={TrendingDown} colorClass="text-emerald-400" bgClass="bg-emerald-500/10" data-id="element-1221" />

        <div className="space-y-3" data-id="element-1222">
          {improved.length > 0 ? improved.map(pattern => <motion.div key={pattern.type} variants={item} className="group flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5 hover:bg-gray-800/60 hover:border-emerald-500/20 transition-all duration-200" data-id="element-1223">
                <div className="flex items-center gap-3" data-id="element-1224">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1225" />
                  <span className="text-gray-200 font-medium group-hover:text-white transition-colors" data-id="element-1226">
                    {pattern.type}
                  </span>
                </div>
                <span className="text-emerald-400 text-sm font-bold bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/10" data-id="element-1227">
                  -{Math.abs(pattern.change)}%
                </span>
              </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-white/5 rounded-xl" data-id="element-1228">
              <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center mb-3 text-gray-600" data-id="element-1229">
                <Minus size={20} data-id="element-1230" />
              </div>
              <p className="text-gray-500 text-sm font-medium" data-id="element-1231">
                No improvements yet
              </p>
            </div>}
        </div>
      </motion.div>

      {/* Stable Column */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-50px'
    }} className="bg-gray-900/40 backdrop-blur-sm rounded-3xl p-6 border border-white/5" data-id="element-1232">
        <ColumnHeader title="Stable" icon={Minus} colorClass="text-amber-400" bgClass="bg-amber-500/10" data-id="element-1233" />

        <div className="space-y-3" data-id="element-1234">
          {stable.length > 0 ? stable.map(pattern => <motion.div key={pattern.type} variants={item} className="group flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5 hover:bg-gray-800/60 hover:border-amber-500/20 transition-all duration-200" data-id="element-1235">
                <div className="flex items-center gap-3" data-id="element-1236">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" data-id="element-1237" />
                  <span className="text-gray-200 font-medium group-hover:text-white transition-colors" data-id="element-1238">
                    {pattern.type}
                  </span>
                </div>
                <span className="text-amber-400 text-sm font-bold bg-amber-500/10 px-2.5 py-1 rounded-lg border border-amber-500/10" data-id="element-1239">
                  ~
                </span>
              </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-white/5 rounded-xl" data-id="element-1240">
              <p className="text-gray-500 text-sm font-medium" data-id="element-1241">
                No stable patterns
              </p>
            </div>}
        </div>
      </motion.div>

      {/* Worsened Column */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-50px'
    }} className="bg-gray-900/40 backdrop-blur-sm rounded-3xl p-6 border border-white/5" data-id="element-1242">
        <ColumnHeader title="Needs Attention" icon={AlertTriangle} colorClass="text-red-400" bgClass="bg-red-500/10" data-id="element-1243" />

        <div className="space-y-3" data-id="element-1244">
          {worsened.length > 0 ? worsened.map(pattern => <motion.div key={pattern.type} variants={item} className="group flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5 hover:bg-gray-800/60 hover:border-red-500/20 transition-all duration-200" data-id="element-1245">
                <div className="flex items-center gap-3" data-id="element-1246">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-1247" />
                  <span className="text-gray-200 font-medium group-hover:text-white transition-colors" data-id="element-1248">
                    {pattern.type}
                  </span>
                </div>
                <span className="text-red-400 text-sm font-bold bg-red-500/10 px-2.5 py-1 rounded-lg border border-red-500/10" data-id="element-1249">
                  +{pattern.change}%
                </span>
              </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-emerald-500/20 bg-emerald-500/5 rounded-xl" data-id="element-1250">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3 text-emerald-400" data-id="element-1251">
                <CheckCircle2 size={20} data-id="element-1252" />
              </div>
              <p className="text-emerald-400 text-sm font-bold" data-id="element-1253">Great job!</p>
              <p className="text-emerald-500/70 text-xs mt-1" data-id="element-1254">
                No new concerns detected
              </p>
            </div>}
        </div>
      </motion.div>
    </div>;
}